import { Button, TopAffix, LoadingIndicator } from '@vrplatform/components';
import {
  useApiQuery,
  useBilling,
  useDashboard,
  useIntercom,
  useTeam,
  useTeamRole,
  useTrpcQuery,
} from '@vrplatform/data';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import { useAppName } from '@vrplatform/theme';
import { Center, Group, Text, Title } from '@mantine/core';
import { useSessionStorage } from '@mantine/hooks';
import type { HyperlineEntities } from '@vrplatform/hyperline-client';
import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { HiddenFeatureIndicator } from '@vrplatform/embed/components';
import { UpgradeSubscriptionPaymentForm } from '../../../../components';

export const STORYBOOK_DASHBOARD_BILLING_SUBSCRIPTION_QUERY_KEY =
  'dashboard-billing-subscription';

const billingStatusesHints: Record<
  HyperlineEntities['Subscription']['status'],
  string
> = {
  active: 'Teams subscription is active',
  archived:
    "Team is locked, because the team's subscription has been archived.",
  cancelled: 'Your subscription has been canceled.', // not used, hint is shown in navigation
  errored: 'Team is locked, because payment failed on teams subscription.',
  paused: 'Team is locked, because teams subscription has been paused.',
  pending: 'Team is locked, because teams subscription is pending.',
  voided: 'Team is locked, because teams subscription has been voided.',
  draft: 'Team is locked, because teams subscription is in draft.',
};

const ViewAffix = ({
  icon,
  title,
}: {
  icon: IconDefinition;
  title: string;
}) => (
  <TopAffix themeColor="orange">
    <Group gap={'xs'}>
      <Icon icon={icon} size={20} color={({ colors }) => colors.orange[8]} />
      <Text size="xs" component="p" m={0}>
        {title}
      </Text>
    </Group>
  </TopAffix>
);

export const DashboardBillingWrapper = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location = useLocation();
  const [dashboard] = useDashboard();
  const isPartnerDashboard = dashboard === 'partner';
  const [{ id: teamId, isOnboarding, type }] = useTeam();
  const { subscription, showBilling, isDirectCustomer } = useBilling();
  const { isVrpAdmin } = useTeamRole();
  const status = subscription.status;
  const [overrideShowDashboard, setOverrideShowDashboard] = useSessionStorage({
    key: `${teamId}_override_show_dashboard`,
    defaultValue: false,
  });

  const showDashboard = useCallback(() => {
    setOverrideShowDashboard(true);
  }, [setOverrideShowDashboard]);

  if (
    !showBilling ||
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/features')
  )
    return children;

  if (
    isDirectCustomer &&
    status !== 'cancelled' &&
    !subscription.paymentMethodType &&
    !(
      type === 'partner' &&
      isPartnerDashboard &&
      (location.pathname === '/members' || location.pathname === '/members/')
    )
  ) {
    if (isVrpAdmin && overrideShowDashboard)
      return (
        <>
          <ViewAffix
            icon="AlertTriangleIcon"
            title={'VRP Admins view only mode (Missing Payment Details)'}
          />
          {children}
        </>
      );

    return (
      <UpgradeSubscriptionPaymentForm
        title="Missing Payment Details"
        showDashboard={showDashboard}
      />
    );
  }

  if (
    status === 'cancelled' ||
    (isDirectCustomer &&
      isOnboarding === false &&
      status === 'pending' &&
      !!subscription.paymentMethodType)
  ) {
    return children;
  }

  if (status !== 'active') return <LockedBilling>{children}</LockedBilling>;

  return children;
};

const LockedBilling = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [{ id: teamId }] = useTeam();
  const [, refetchTeam] = useTeam();
  const {
    subscription: { status: billingStatus },
  } = useBilling();

  const [isUnlocked, setIsUnlocked] = useSessionStorage({
    key: `tb-${teamId}`, // temporary billing unlock
    defaultValue: false,
  });

  // TODO(PRO-16515): poll `/me` instead once billingSubscriptionStatus is
  // part of the bootstrap contract
  const { data: subscription } = useApiQuery(
    async (api, signal) => {
      const result = await api.GET('/team', { signal });

      if (result.error) throw result.error;

      return {
        billingStatus: result.data.billingSubscriptionStatus,
      };
    },
    {
      queryGroup: 'teams',
      uniqueQueryKey: STORYBOOK_DASHBOARD_BILLING_SUBSCRIPTION_QUERY_KEY,
      storybookQueryKey: STORYBOOK_DASHBOARD_BILLING_SUBSCRIPTION_QUERY_KEY,
      variables: { teamId },
      skip: !teamId || billingStatus === 'active',
      poll: {
        isCompleted: (data) =>
          data?.billingStatus === 'active' ||
          data?.billingStatus === 'cancelled',
      },
    }
  );

  const unlock = useCallback(() => {
    setIsUnlocked(true);
  }, [setIsUnlocked]);

  useEffect(() => {
    if (
      subscription?.billingStatus &&
      subscription.billingStatus !== billingStatus
    )
      refetchTeam();
  }, [subscription?.billingStatus, billingStatus, refetchTeam]);

  if (
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/features')
  )
    return children;

  if (isUnlocked)
    return (
      <>
        <ViewAffix
          icon="CircleDollarIcon"
          title={billingStatusesHints[billingStatus]}
        />
        {children}
      </>
    );

  if (billingStatus === 'pending')
    return (
      <Center
        mih="90%"
        sx={(theme) => ({
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing.md,
          textAlign: 'center',
        })}
      >
        <LoadingIndicator size="md" type="dots" mb="xl" />

        <Title order={2} c="neutral">
          Awaiting Subscription Confirmation
        </Title>
        <Text component="p" c="gray" my="sm" maw={550}>
          We are currently confirming your payment method. If this takes longer
          than expected, please reach out to us using Help & Support.
        </Text>
        <SupportButton />
        <HiddenFeatureIndicator permission="vrp-admin">
          <Button onClick={unlock}>Show data for current session</Button>
        </HiddenFeatureIndicator>
      </Center>
    );

  return <BillingError handleUnlock={unlock} />;
};

const BillingError = ({ handleUnlock }: { handleUnlock: () => void }) => {
  const { appName } = useAppName();
  const { isTeamAdmin } = useTeamRole();
  const { isPartnerClient, subscription } = useBilling();

  const status = subscription.status;

  const info: Record<
    NonNullable<typeof status>,
    {
      title: string;
      description: string;
    }
  > = useMemo(() => {
    const action = isTeamAdmin
      ? `Please update your billing to view your data on ${appName}.`
      : 'Please reach out to your team admin to update the billing.';

    return {
      active: {
        title: 'Your subscription is active',
        description: `Please refresh the window to access your data. ${action}`,
      },
      archived: {
        title: 'Your subscription has been archived',
        description: `The team's subscription has been archived. ${action}`,
      },
      cancelled: {
        title: 'Your subscription has been canceled',
        description: `The team's subscription has been canceled. ${action}`,
      },
      errored: {
        title: "There was an issue with the team's subscription",
        description: `We have found an issue with the team's subscription. ${action}`,
      },
      paused: {
        title: 'Your subscription has been paused',
        description: `Your subscription was recently paused. ${action}`,
      },
      pending: {
        title: 'Your subscription is pending',
        description:
          'We are currently confirming your payment method. If this takes longer than expected, please reach out to us using Help & Support.',
      },
      voided: {
        title: 'Your subscription has been voided',
        description: `Your subscription was recently voided. ${action}`,
      },
      draft: {
        title: 'Your subscription is in draft',
        description: `Your subscription is in draft. ${action}`,
      },
    };
  }, [isTeamAdmin, appName]);

  const defaultTitle = 'Subscription required';
  const defaultDescription = `We have found in an issue with the team's subscription.`;

  return (
    <CenterContent>
      <Icon icon="Lock2Icon" color={({ colors }) => colors.red[6]} size={40} />
      <Title order={2} c="neutral">
        {status ? info[status]?.title || defaultTitle : defaultTitle}
      </Title>
      <Text component="p" c="gray" my="sm" maw={550}>
        {status
          ? info[status]?.description || defaultDescription
          : defaultDescription}
      </Text>
      {isTeamAdmin && !isPartnerClient && <HyperlinePortalButton />}
      <HiddenFeatureIndicator permission="vrp-admin">
        <Button onClick={handleUnlock} variant="primary" leftIcon="Lock2Icon">
          Show data for current session
        </Button>
      </HiddenFeatureIndicator>
    </CenterContent>
  );
};

const CenterContent = ({ children }: { children: ReactNode }) => (
  <Center
    mih="90%"
    sx={(theme) => ({
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing.md,
      textAlign: 'center',
    })}
  >
    {children}
  </Center>
);

const HyperlinePortalButton = () => {
  const [{ id: teamId }] = useTeam();
  const { data, loading } = useTrpcQuery('getHyperlinePortal', {
    tenantId: teamId,
  });

  return (
    <Button
      color="red"
      variant="filled"
      loading={loading}
      disabled={!loading && !data?.url}
      component={'a'}
      href={data?.url || ''}
      rightIcon={'ArrowRightIcon'}
      sx={{
        path: {
          stroke: 'white',
        },
      }}
    >
      Manage billing
    </Button>
  );
};

const SupportButton = () => {
  const { show } = useIntercom();

  return (
    <Button variant="light" onClick={show} leftIcon={'CommentQuestionIcon'}>
      Help & Support
    </Button>
  );
};
